/**
 * Maps admin route path prefixes to their required module id.
 *
 * Access is MODULE-level only: a user with the module id in `user_access`
 * gets full access to every route under that prefix. Role id 1 (Superadmin)
 * bypasses all module checks.
 *
 * Keep the longest prefixes first — lookup iterates top-down and returns
 * the first prefix match against the current pathname (relative to /Auth).
 * Routes not listed here are considered open to every authenticated user.
 */

export interface RouteModuleRule {
  prefix: string;
  module: string;
  allowedRoles?: number[];
}

export const ROUTE_MODULE_MAP: RouteModuleRule[] = [
  // Longer prefixes first
  { prefix: "/unified-content", module: "29" },
  { prefix: "/epaper-settings", module: "33" },
  { prefix: "/VidyaScreeen", module: "13" },
  { prefix: "/NjayarScreen", module: "18" },
  { prefix: "/SubCategory", module: "8" },
  { prefix: "/AddScreen", module: "24" },
  { prefix: "/EpaperForm", module: "7" },
  { prefix: "/EpaperCommonPages", module: "7" },
  { prefix: "/epaper-users", module: "6" },
  { prefix: "/createwebStories", module: "20" },
  { prefix: "/storiesList", module: "20" },
  { prefix: "/webstories", module: "20" },
  { prefix: "/categories-form", module: "2" },
  { prefix: "/categories", module: "2" },
  { prefix: "/editorial", module: "26" },
  { prefix: "/gulf-videos", module: "28" },
  { prefix: "/special-page", module: "12" },
  { prefix: "/highlights", module: "10" },
  { prefix: "/campaign", module: "27" },
  { prefix: "/election-items", module: "11" },
  { prefix: "/election-data", module: "11" },
  { prefix: "/election", module: "11" },
  { prefix: "/insnap-form", module: "15" },
  { prefix: "/insnap-imageList", module: "15" },
  { prefix: "/inSnap", module: "15" },
  { prefix: "/QuestionForm", module: "16" },
  { prefix: "/QuizQuestion", module: "16" },
  { prefix: "/QuizCampForm", module: "16" },
  { prefix: "/QuizAnswers", module: "16" },
  { prefix: "/Quiz", module: "16" },
  { prefix: "/form-answers", module: "21" },
  { prefix: "/form", module: "21" },
  { prefix: "/match-predictions", module: "32" },
  { prefix: "/frames", module: "5" },
  { prefix: "/podcast", module: "3" },
  { prefix: "/reels", module: "4" },
  { prefix: "/Article", module: "23" },
  { prefix: "/Epaper", module: "7" },
  { prefix: "/polls", module: "9" },
  { prefix: "/poll", module: "9" },
  { prefix: "/banner", module: "19" },
  { prefix: "/tags", module: "14" },
  { prefix: "/agents", module: "30" },
  { prefix: "/home", module: "1" },
  { prefix: "/users", module: "17"},
  { prefix: "/release-notifications", module: "25" },
  { prefix: "/settings", module: "22" },
];

/**
 * Paths that every authenticated user can access (no module check).
 * Kept short — home, profile, change-password, sessions.
 */
const OPEN_PATHS = [
  "/",
  "/profile",
  "/editprofile",
  "/ChangePassword",
  "/sessions",
];

export function resolveRouteRule(
  relativePath: string
): RouteModuleRule | null {
  if (OPEN_PATHS.some((p) => relativePath === p || relativePath.startsWith(p + "/"))) {
    return null;
  }
  for (const rule of ROUTE_MODULE_MAP) {
    if (
      relativePath === rule.prefix ||
      relativePath.startsWith(rule.prefix + "/") ||
      relativePath.startsWith(rule.prefix + "?")
    ) {
      return rule;
    }
  }
  return null;
}

export function userHasModuleAccess(
  user: { user_access?: string; role?: { id?: number } | number } | null,
  rule: RouteModuleRule | null
): boolean {
  if (!rule) return true;
  if (!user) return false;
  const roleId =
    typeof user.role === "object" ? user.role?.id : (user.role as number);
  if (roleId === 1) return true; // superadmin bypass
  if (rule.allowedRoles && !rule.allowedRoles.includes(Number(roleId))) {
    return false;
  }
  const access = (user.user_access || "").split("|").filter(Boolean);
  return access.includes(String(rule.module));
}
